import { CardRow, CardRowProps } from 'components/CardRow';
import { MenuProps } from 'components/Menu';
import { Txt } from 'components/Txt';
import { formatDate } from 'date-fns';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { filterArrayEmpty, isoDateStringToDate } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { View } from 'react-native';
import {
  ContactAddedVia,
  EngageFlatContact,
  FlatContactTypes,
} from 'types/Engage';
type Props = {
  contact: EngageFlatContact;
  onPress?: (contact: EngageFlatContact) => void;
  menuOptions?: MenuProps['options'];
  checkedValue?: CardRowProps['checkedValue'];
  contactDetailsOnOwnLine?: boolean;
  showAddedInfo?: boolean;
  showArchivedInfo?: boolean;
};

export const EngageContactCard: React.FC<Props> = ({
  contact,
  onPress,
  menuOptions,
  checkedValue,
  contactDetailsOnOwnLine = false,
  showAddedInfo = false,
  showArchivedInfo = false,
}) => {
  const { archivedTimestamp, archivedVia } = contact;
  const contactType = FlatContactTypes[contact.contactType];
  const confirmedDateLabel = contact.confirmedDate
    ? formatDate(
        isoDateStringToDate(contact.confirmedDate),
        UI_DATE_FORMAT_LONG,
      )
    : null;
  const confirmationPillBackground = contact.isConfirmed
    ? theme.colors.green20
    : theme.colors.red20;
  const confirmationPillTextColor = contact.isConfirmed
    ? theme.colors.black
    : theme.colors.black;
  const confirmationLabel = contact.isConfirmed
    ? confirmedDateLabel
      ? `Confirmed ${confirmedDateLabel}`
      : 'Confirmed'
    : 'Not Confirmed';

  const TxtType = (
    <Txt
      style={{
        color: theme.colors.fieldValueText,
      }}
      size="xsmall"
    >
      {contactType.name}
    </Txt>
  );

  const TxtAdded = (
    <Txt
      style={{
        color: theme.colors.fieldValueText,
      }}
      size="xsmall"
    >
      {`Added ${formatDate(
        isoDateStringToDate(contact.addedTimestamp),
        UI_DATE_FORMAT_LONG,
      )} via ${ContactAddedVia[contact.addedVia].name}`}
    </Txt>
  );

  return (
    <CardRow
      onPress={onPress ? () => onPress(contact) : undefined}
      menuOptions={menuOptions}
      checkedValue={checkedValue}
    >
      <Txt>
        <Txt key="name" weight="medium">
          {contact.isUnder16
            ? 'Under 16'
            : `${contact.firstName} ${contact.lastName}`}
        </Txt>
        {!!contact.email || !!contact.phone
          ? contactDetailsOnOwnLine
            ? '\n'
            : ' • '
          : ''}
        {[contact.email, contact.phone].filter(filterArrayEmpty).join(' • ')}
      </Txt>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: theme.paddingHorizontalHalf,
          flexWrap: 'wrap',
        }}
      >
        {TxtType}
        <View
          style={{
            backgroundColor: confirmationPillBackground,
            borderRadius: 100,
            paddingHorizontal: theme.paddingVerticalQuarter,
            paddingVertical: theme.paddingVerticalQuarter / 2,
          }}
        >
          <Txt
            style={{
              color: confirmationPillTextColor,
            }}
            size="xxxsmall"
            weight="bold"
            lineHeightNone={true}
          >
            {confirmationLabel}
          </Txt>
        </View>
        {contact.hasSupportNeeds && (
          <View
            style={{
              backgroundColor: theme.colors.orange30,
              borderRadius: 100,
              paddingHorizontal: theme.paddingVerticalQuarter,
              paddingVertical: theme.paddingVerticalQuarter / 2,
            }}
          >
            <Txt
              style={{ textAlign: 'center', color: theme.colors.black }}
              size="xxxsmall"
              weight="bold"
              lineHeightNone={true}
            >
              Has Support Needs
            </Txt>
          </View>
        )}
      </View>
      {showAddedInfo && (
        <Txt
          style={{
            color: theme.colors.fieldValueText,
          }}
          size="xsmall"
        >
          {TxtAdded}
          {showArchivedInfo &&
            !!archivedTimestamp &&
            !!archivedVia &&
            ` • Archived ${formatDate(
              isoDateStringToDate(archivedTimestamp),
              UI_DATE_FORMAT_LONG,
            )} via ${ContactAddedVia[archivedVia].name}`}
        </Txt>
      )}
    </CardRow>
  );
};
